import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { FunctionComponent, useMemo } from "react";
import style from "./pagination.module.scss";

const PageLink: FunctionComponent<PageLinkProps> = ({
  pageNumber,
  currentPage,
  baseUrl,
  children,
  disabled,
  shouldPrefetch,
  useQueryParamPagination = false,
  variant = "page",
}) => {
  const searchParams = useSearchParams();

  const linkUrl = useMemo<string>(() => {
    const uri = `${baseUrl}${useQueryParamPagination ? "" : pageNumber}`;

    const params = new URLSearchParams();
    if (searchParams) {
      searchParams.forEach((value, key) => {
        params.append(key, value);
      });
    }

    if (useQueryParamPagination && params.get("pageNumber") === null) {
      params.append("pageNumber", pageNumber.toString());
    } else if (useQueryParamPagination && params.get("pageNumber") !== null) {
      params.set("pageNumber", pageNumber.toString());
    }

    return `${uri}${params.size > 0 ? `?${params.toString()}` : ""}`;
  }, [baseUrl, pageNumber, useQueryParamPagination, searchParams]);

  if (variant === "nav") {
    const isNext = children === "Next";

    if (disabled) {
      return (
        <span className={`${style.navButton} ${style.navButtonDisabled}`}>
          {!isNext && <span className={`${style.caret} ${style.caretLeft}`} />}
          {children}
          {isNext && <span className={`${style.caret} ${style.caretRight}`} />}
        </span>
      );
    }

    return (
      <Link className={style.navButton} href={linkUrl} prefetch={shouldPrefetch}>
        {!isNext && <span className={`${style.caret} ${style.caretLeft}`} />}
        {children}
        {isNext && <span className={`${style.caret} ${style.caretRight}`} />}
      </Link>
    );
  }

  if (currentPage === pageNumber) {
    return <span className={style.currentPage}>{pageNumber}</span>;
  }

  return (
    <Link className={style.pageLink} href={linkUrl} prefetch={shouldPrefetch}>
      {children}
    </Link>
  );
};

export type PageLinkProps = {
  pageNumber: number;
  currentPage: number;
  baseUrl: string;
  children: string;
  className?: string;
  disabled?: boolean;
  shouldPrefetch: boolean;
  useQueryParamPagination?: boolean;
  variant?: "page" | "nav";
};

export default PageLink;
